// New Appointment Dialog for POS
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  CalendarDays, User, Phone, Clock, Scissors, UserCheck, Plus, Loader2 
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from './constants';

// Available time slots
const TIME_SLOTS = [
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
  '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
  '18:00', '18:30', '19:00', '19:30'
];

export function NewAppointmentDialog({ 
  isOpen, 
  onClose, 
  branchId, 
  barbers, 
  services,
  getAuthHeaders,
  onSuccess 
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [availableSlots, setAvailableSlots] = useState(TIME_SLOTS);
  const [calendarOpen, setCalendarOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    client_name: '',
    client_whatsapp: '',
    date: null,
    time: '',
    barber_id: '',
    service_id: '',
    notes: ''
  });

  // Filter out barbers whose rest_day matches the selected date's weekday.
  // JS getDay: 0=Sunday..6=Saturday. Backend rest_day format: 0=Monday..6=Sunday.
  // Convert JS getDay -> backend format: (js + 6) % 7
  const availableBarbers = (() => {
    if (!formData.date) return barbers;
    const jsDow = new Date(formData.date).getDay();
    const dow = (jsDow + 6) % 7; // Monday=0..Sunday=6
    return barbers.filter(b => b.rest_day === null || b.rest_day === undefined || Number(b.rest_day) !== dow);
  })();

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        client_name: '',
        client_whatsapp: '',
        date: null,
        time: '',
        barber_id: '',
        service_id: '',
        notes: ''
      });
      setAvailableSlots(TIME_SLOTS);    }
  }, [isOpen]);

  // Fetch available slots when date changes
  useEffect(() => {
    const fetchAvailableSlots = async () => {
      if (!formData.date || !branchId) return;
      
      try {
        const dateStr = format(formData.date, 'yyyy-MM-dd');
        const response = await axios.get(
          `${API}/appointments/available-slots?branch_id=${branchId}&date=${dateStr}`,
          { headers: getAuthHeaders() }
        );
        setAvailableSlots(response.data.available_slots || TIME_SLOTS);
      } catch (error) {
        // error handled
        setAvailableSlots(TIME_SLOTS);
      }
    };

    fetchAvailableSlots();
  }, [formData.date, branchId, getAuthHeaders]);

  // Clear barber_id if the currently selected barber becomes unavailable after a date change
  useEffect(() => {
    if (!formData.barber_id || !formData.date) return;
    const stillAvailable = availableBarbers.some(b => b.id === formData.barber_id);
    if (!stillAvailable) {
      setFormData(prev => ({ ...prev, barber_id: '' }));
    }
  }, [formData.date, formData.barber_id, availableBarbers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.client_name || !formData.client_whatsapp || !formData.date || !formData.time) {
      toast.error('Por favor completa los campos requeridos');
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        branch_id: branchId,
        client_name: formData.client_name,
        client_whatsapp: formData.client_whatsapp,
        date: format(formData.date, 'yyyy-MM-dd'),
        time: formData.time,
        barber_id: formData.barber_id || null,
        service_id: formData.service_id || null,
        notes: formData.notes || null
      };

      await axios.post(`${API}/appointments`, payload, {
        headers: getAuthHeaders()
      });

      toast.success('Cita agendada exitosamente');
      onSuccess?.();
      onClose();
    } catch (error) {
      // error handled
      const message = error.response?.data?.detail || 'Error al agendar la cita';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white font-heading flex items-center gap-2">
            <Plus className="w-5 h-5 text-gold" />
            Nueva Cita
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Client Name */}
          <div className="space-y-2">
            <Label className="text-zinc-400 flex items-center gap-2">
              <User className="w-4 h-4" />
              Nombre del Cliente *
            </Label>
            <Input
              value={formData.client_name}
              onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
              placeholder="Nombre completo"
              className="bg-zinc-800 border-zinc-700 text-white"
              data-testid="appointment-client-name"
            />
          </div>

          {/* WhatsApp */}
          <div className="space-y-2">
            <Label className="text-zinc-400 flex items-center gap-2">
              <Phone className="w-4 h-4" />
              WhatsApp *
            </Label>
            <Input
              value={formData.client_whatsapp}
              onChange={(e) => setFormData({ ...formData, client_whatsapp: e.target.value })}
              placeholder="55 1234 5678"
              className="bg-zinc-800 border-zinc-700 text-white"
              data-testid="appointment-whatsapp"
            />
          </div>

          {/* Date */}
          <div className="space-y-2">
            <Label className="text-zinc-400 flex items-center gap-2">
              <CalendarDays className="w-4 h-4" />
              Fecha *
            </Label>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700"
                  data-testid="appointment-date-btn"
                >
                  <CalendarDays className="w-4 h-4 mr-2 text-zinc-500" />
                  {formData.date 
                    ? format(formData.date, "EEEE d 'de' MMMM", { locale: es })
                    : 'Seleccionar fecha'
                  }
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-700" align="start">
                <Calendar
                  mode="single"
                  selected={formData.date}
                  onSelect={(date) => {
                    setFormData({ ...formData, date, time: '' });
                    setCalendarOpen(false);
                  }}
                  disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                  locale={es}
                  className="bg-zinc-900 text-white"
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Time */}
          <div className="space-y-2">
            <Label className="text-zinc-400 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Hora *
            </Label>
            <Select
              value={formData.time}
              onValueChange={(value) => setFormData({ ...formData, time: value })}
              disabled={!formData.date}
            >
              <SelectTrigger 
                className="bg-zinc-800 border-zinc-700 text-white"
                data-testid="appointment-time-select"
              >
                <SelectValue placeholder={formData.date ? "Seleccionar hora" : "Primero selecciona fecha"} />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700 max-h-[200px]">
                {availableSlots.map((slot) => (
                  <SelectItem key={slot} value={slot} className="text-white hover:bg-zinc-700">
                    {slot}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Barber (Optional) */}
          <div className="space-y-2">
            <Label className="text-zinc-400 flex items-center gap-2">
              <UserCheck className="w-4 h-4" />
              Barbero (opcional)
            </Label>
            <Select
              value={formData.barber_id}
              onValueChange={(value) => setFormData({ ...formData, barber_id: value === 'none' ? '' : value })}
            >
              <SelectTrigger 
                className="bg-zinc-800 border-zinc-700 text-white"
                data-testid="appointment-barber-select"
              >
                <SelectValue placeholder="Sin preferencia" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700">
                <SelectItem value="none" className="text-zinc-400">Sin preferencia</SelectItem>
                {availableBarbers.map((barber) => (
                  <SelectItem key={barber.id} value={barber.id} className="text-white hover:bg-zinc-700" data-testid={`appointment-barber-option-${barber.id}`}>
                    {barber.name}
                  </SelectItem>
                ))}
                {formData.date && availableBarbers.length === 0 && (
                  <div className="px-3 py-2 text-xs text-zinc-500 italic">
                    Ningún barbero disponible ese día
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Service (Optional) */}
          <div className="space-y-2">
            <Label className="text-zinc-400 flex items-center gap-2">
              <Scissors className="w-4 h-4" />
              Servicio (opcional)
            </Label>
            <Select
              value={formData.service_id}
              onValueChange={(value) => setFormData({ ...formData, service_id: value === 'none' ? '' : value })}
            >
              <SelectTrigger 
                className="bg-zinc-800 border-zinc-700 text-white"
                data-testid="appointment-service-select"
              >
                <SelectValue placeholder="Sin especificar" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700">
                <SelectItem value="none" className="text-zinc-400">Sin especificar</SelectItem>
                {services.map((service) => (
                  <SelectItem key={service.id} value={service.id} className="text-white hover:bg-zinc-700">
                    {service.name} - ${service.price}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label className="text-zinc-400">Notas (opcional)</Label>
            <Input
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Notas adicionales..."
              className="bg-zinc-800 border-zinc-700 text-white"
              data-testid="appointment-notes"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 border-zinc-700 text-zinc-400 hover:bg-zinc-800"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="flex-1 bg-gold hover:bg-gold/90 text-black"
              data-testid="appointment-submit-btn"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Agendar Cita
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
